import { useEffect, useRef, useState } from 'react'
import { formatDistanceToNowStrict } from 'date-fns'
import { signalsAPI } from '../services/api'
import type { ScanStatus } from '../types'
import Tooltip from './Tooltip'
import { DASHBOARD_GLOSSARY } from '../config/glossary'

const POLL_INTERVAL_MS = 60_000

// The scan runs hourly. Past two intervals the scanner has missed at least one
// run, so the dot stops pulsing rather than implying all is well.
export const SCAN_STALE_AFTER_MS =2 * 60 * 60 * 1000

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/**
 * One quiet status line: is the market scanner alive, and what is it watching.
 * Display only.
 *
 * The open count is passed in rather than read from the scan status, so the
 * Dashboard can hand this line and its Open Signals tile the same number.
 */
const ScanHeartbeat = ({ openSignals }: { openSignals: number }) => {
  const [status, setStatus] = useState<ScanStatus | null>(null)
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true

    const load = async () => {
      try {
        const data = await signalsAPI.scanStatus()
        if (isMounted.current) setStatus(data)
      } catch (error) {
        // Keep the last reading; the next poll retries.
        console.error('Scan status refresh failed:', error)
      }
    }

    load()
    const timer = setInterval(load, POLL_INTERVAL_MS)
    return () => {
      isMounted.current = false
      clearInterval(timer)
    }
  }, [])

  if (!status) return null

  const lastScan = status.last_scan_at ? new Date(status.last_scan_at) : null
  const state = !lastScan ? 'none' : Date.now() - lastScan.getTime() > SCAN_STALE_AFTER_MS ? 'stale' : 'live'

  return (
    <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400" data-testid="scan-heartbeat" data-state={state}>
      <span className="relative flex h-2 w-2" aria-hidden="true">
        {state === 'live' && (
          <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
        )}
        <span
          className={`relative inline-flex h-2 w-2 rounded-full ${
            state === 'live' ? 'bg-green-500' : state === 'stale' ? 'bg-amber-500' : 'bg-gray-400'
          }`}
        />
      </span>
      <span>
        {'Last market scan: '}
        {lastScan ? (
          `${formatDistanceToNowStrict(lastScan)} ago`
        ) : (
          <Tooltip content={DASHBOARD_GLOSSARY.lastScanNone}>not yet recorded</Tooltip>
        )}
        {` · scanning ${plural(status.symbols_scanned, 'symbol')} · ${plural(openSignals, 'signal')} open`}
      </span>
    </p>
  )
}

export default ScanHeartbeat
